import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";

const SubscriptionPlans = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const plans = [
    {
      name: "Basic",
      prices: ["₹899", "₹2,757", "₹9,000"],
      features: [
        "5 users",
        "Broadcast using Excel",
        "30 integrations",
        "Unlimited templates",
        "Basic API",
        "10 chat builders",
        "Team inbox",
        "30 custom fields",
        "5 tags",
        "Schedule campaign",
        "CRM integration",
        "Report view only",
        "Form with watermark",
      ],
      popular: false,
      color: theme.palette.primary.main,
    },
    {
      name: "Growth",
      prices: ["₹1,999", "₹6,500", "₹21,000"],
      features: [
        "10 users",
        "Chatbot report",
        "Carousel message formats",
        "WhatsApp Flows/Forms",
        "Catalog",
        "Unlimited conversations",
        "Unlimited tags",
        "Unlimited contacts",
        "Automatic retry",
        "User-wise permissions",
        "Advanced messaging API",
        "Webhooks",
        "Number masking",
        "Report export",
        "Form without watermark",
      ],
      popular: true,
      color: theme.palette.secondary.main,
    },
    {
      name: "Advanced",
      prices: ["₹3,000", "₹9,400", "₹32,000"],
      features: [
        "25 users",
        "Drip campaign",
        "Advanced chatbot",
        "Native payment",
        "Multiple WhatsApp numbers/channels",
        "AI rewrite",
        "Recommended for Brands with 2 Lac+ Users",
        "Dedicated Account Manager",
        "Priority Customer Support",
        "Higher Messaging Speed",
        "Drag and drop builder",
        "Advanced webhook",
        "7 custom events",
        "Report for meta ads leads",
      ],
      popular: false,
      color: theme.palette.success.main,
    },
  ];

  
  return (
    <Box
      sx={{
        py: 8,
        px: isMobile ? 2 : 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography
        variant="h3"
        component="h2"
        align="center"
        gutterBottom
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: 4,
        }}
      >
        Choose Your Plan
      </Typography>

      <Typography
        variant="subtitle1"
        align="center"
        sx={{
          mb: 6,
          color: theme.palette.text.secondary,
        }}
      >
        Select the perfect plan for your business needs
      </Typography>

      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: plan.popular ? `2px solid ${plan.color}` : "none",
                boxShadow: plan.popular ? theme.shadows[8] : theme.shadows[2],
                transform: plan.popular ? "scale(1.02)" : "scale(1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "scale(1.03)",
                  boxShadow: theme.shadows[10],
                },
              }}
            >
              {plan.popular && (
                <Box
                  sx={{
                    backgroundColor: plan.color,
                    color: theme.palette.getContrastText(plan.color),
                    textAlign: "center",
                    py: 1,
                    fontWeight: "bold",
                  }}
                >
                  <StarIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                  Most Popular
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1 }}>
                <Typography
                  variant="h4"
                  component="h3"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: plan.color,
                  }}
                >
                  {plan.name}
                </Typography>

                <Box sx={{ textAlign: "center", mb: 3 }}>
                  {plan.prices.map((price, i) => (
                    <Typography
                      key={i}
                      variant={i === 1 ? "h5" : "body1"}
                      component="div"
                      sx={{
                        fontWeight: i === 1 ? 700 : 400,
                        color:
                          i === 1
                            ? theme.palette.text.primary
                            : theme.palette.text.secondary,
                      }}
                    >
                      {price}
                    </Typography>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <List>
                  {plan.features.map((feature, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon sx={{ color: plan.color }} />
                      </ListItemIcon>
                      <Typography variant="body2">{feature}</Typography>
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <Box sx={{ p: 2, textAlign: "center" }}>
                <Button
                  variant={plan.popular ? "contained" : "outlined"}
                  size="large"
                  sx={{
                    backgroundColor: plan.popular ? plan.color : "transparent",
                    color: plan.popular
                      ? theme.palette.getContrastText(plan.color)
                      : plan.color,
                    borderColor: plan.color,
                    "&:hover": {
                      backgroundColor: plan.color,
                      color: theme.palette.getContrastText(plan.color),
                    },
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SubscriptionPlans;
